import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Download, Maximize2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

// Import shared components from Presentation
const getBarColor = (score) => {
  if (score >= 85) return "#00FF87";
  if (score >= 70) return "#00D4FF";
  if (score >= 50) return "#FFD600";
  return "#FF3366";
};
const getStatusIcon = (answer) => {
  if (answer === "yes") return { color: "#00FF87", symbol: "\u2713", bg: "rgba(0,255,135,0.12)" };
  if (answer === "partial") return { color: "#FFD600", symbol: "!", bg: "rgba(255,214,0,0.12)" };
  if (answer === "no") return { color: "#FF3366", symbol: "\u2717", bg: "rgba(255,51,102,0.12)" };
  return { color: "#666", symbol: "\u2014", bg: "rgba(102,102,102,0.1)" };
};

const SECTION_DISPLAY = {
  discover: { label: "Desktop Version", subtitle: "Discover", icon: "\uD83D\uDDA5" },
  mobile: { label: "Mobile Version", subtitle: "Discover Mobile", icon: "\uD83D\uDCF1" },
  drive: { label: "Marketing Readiness", subtitle: "Drive", icon: "\uD83D\uDCE3" },
  dominate: { label: "SEO Readiness", subtitle: "Dominate", icon: "\uD83D\uDD0D" },
  discourse: { label: "Social Media", subtitle: "Discourse", icon: "\uD83D\uDCAC" },
  deliver: { label: "Packaging Experience", subtitle: "Deliver", icon: "\uD83D\uDCE6" },
  delight: { label: "Customer Support", subtitle: "Delight", icon: "\u2764\uFE0F" },
};

function AnimatedNumber({ value, suffix = "%" }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = Math.round(value);
    if (end === 0) { setDisplay(0); return; }
    const step = Math.max(1, Math.floor(end / 50));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplay(end); clearInterval(timer); }
      else setDisplay(start);
    }, 30);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{display}{suffix}</span>;
}

function AnimBar({ score, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(score), 200 + delay);
    return () => clearTimeout(t);
  }, [score, delay]);
  return (
    <div className="h-8 rounded-full bg-zinc-800/60 overflow-hidden flex-1">
      <div className="h-full rounded-full transition-all ease-out" style={{ width: `${width}%`, backgroundColor: color, transitionDuration: "1.2s", transitionDelay: `${delay}ms` }} />
    </div>
  );
}

function DonutChart({ score, size = 160, strokeWidth = 14, color }) {
  const [offset, setOffset] = useState(100);
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  useEffect(() => {
    const t = setTimeout(() => setOffset(100 - score), 300);
    return () => clearTimeout(t);
  }, [score]);
  const c = color || getBarColor(score);
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#27272A" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={strokeWidth}
        strokeDasharray={circ} strokeDashoffset={(offset / 100) * circ}
        strokeLinecap="round" style={{ transition: "stroke-dashoffset 1.5s ease-out" }} />
    </svg>
  );
}

function Slide({ active, children }) {
  return (
    <div className={cn(
      "absolute inset-0 flex flex-col transition-all duration-500",
      active ? "opacity-100 translate-x-0 z-10" : "opacity-0 translate-x-8 z-0 pointer-events-none"
    )}>
      {children}
    </div>
  );
}

// Simplified slide components for shared view (same visual, no edit capabilities)
function CoverSlide({ audit, active }) {
  const ci = audit.client_info || {};
  return (
    <Slide active={active}>
      <div className="flex-1 flex flex-col items-center justify-center px-12 relative">
        <div className="absolute top-6 left-8 right-8 flex justify-between items-start">
          <div className="text-2xl font-bold text-white/80">{ci.store_name}</div>
          <img src={LOGO_URL} alt="DI" className="h-10 opacity-80" />
        </div>
        <div className="inline-block px-6 py-2 rounded-full border border-emerald-500/40 text-emerald-400 text-sm tracking-widest mb-6">DIGITAL IMPRESSIONS</div>
        <h1 className="text-6xl font-black text-white tracking-tight mb-2">IMPRESS 360</h1>
        <h2 className="text-5xl font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-6">Audit Report</h2>
        <p className="text-zinc-400 text-lg">{ci.store_name} | {ci.audit_date ? new Date(ci.audit_date).toLocaleDateString('en-US', {month: 'long', year: 'numeric'}) : ''}</p>
        <div className="mt-8 px-6 py-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 text-zinc-300 text-sm italic">"From audit insights to smarter growth decisions."</div>
      </div>
    </Slide>
  );
}

function SectionComparisonSlide({ audit, active }) {
  const sectionOrder = ["discover", "mobile", "drive", "dominate", "discourse", "deliver", "delight"];
  const sorted = sectionOrder.filter(id => audit.sections?.[id]).map(id => ({ id, ...audit.sections[id] })).sort((a, b) => a.score - b.score);
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Section Score Comparison</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="flex-1 flex flex-col justify-center gap-4">
          {sorted.map((s, i) => (
            <div key={s.id} className="flex items-center gap-4">
              <span className="text-zinc-300 text-sm w-44 text-right shrink-0">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span>
              <AnimBar score={s.score} color={getBarColor(s.score)} delay={i * 120} />
              <span className="text-white font-bold text-sm w-12">{Math.round(s.score)}%</span>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}

function SectionDetailSlide({ audit, sectionId, questionDefs, insights, active }) {
  const sData = audit.sections?.[sectionId] || {};
  const display = SECTION_DISPLAY[sectionId] || {};
  const answers = sData.answers || [];
  const qMap = {};
  (questionDefs || []).forEach(q => { qMap[q.id] = q; });
  const scored = answers.map(a => {
    const qDef = qMap[a.question_id] || {};
    let priority = 0;
    if (a.answer === "no") priority = 100 + (qDef.weight || 1) * 10;
    else if (a.answer === "partial") priority = 50 + (qDef.weight || 1) * 10;
    else if (a.answer === "yes") priority = (qDef.weight || 1);
    return { ...a, text: qDef.text || a.question_id, weight: qDef.weight || 1, priority };
  }).sort((a, b) => b.priority - a.priority).slice(0, 7);
  const yesCount = answers.filter(a => a.answer === "yes").length;
  const noCount = answers.filter(a => a.answer === "no").length;
  const partialCount = answers.filter(a => a.answer === "partial").length;
  const sInsight = insights?.section_insights?.[sectionId]?.insight || "";
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-3xl font-black text-white flex items-center gap-3"><span className="text-2xl">{display.icon}</span> {display.label}</h2>
            <p className="text-zinc-500 text-sm">{display.subtitle}</p>
          </div>
          <span className="text-5xl font-black" style={{ color: getBarColor(sData.score || 0) }}>{Math.round(sData.score || 0)}%</span>
        </div>
        <div className="grid grid-cols-5 gap-4 flex-1">
          <div className="col-span-3 flex flex-col gap-2 overflow-hidden">
            {scored.map((q, i) => {
              const st = getStatusIcon(q.answer);
              return (
                <div key={q.question_id} className="flex items-center gap-3 rounded-xl bg-zinc-800/40 border border-zinc-700/30 px-4 py-2.5">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0" style={{ background: st.bg, color: st.color, border: `2px solid ${st.color}` }}>{st.symbol}</div>
                  <span className="text-zinc-300 text-xs leading-snug">{q.text}</span>
                </div>
              );
            })}
          </div>
          <div className="col-span-2 flex flex-col gap-3">
            <div className="rounded-xl bg-zinc-800/40 border border-zinc-700/30 p-4">
              <p className="text-xs text-cyan-400 font-bold mb-3">Status Summary</p>
              {[{ label: "Passed", count: yesCount, color: "#00FF87" }, { label: "Partial", count: partialCount, color: "#FFD600" }, { label: "Failed", count: noCount, color: "#FF3366" }].map(item => (
                <div key={item.label} className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
                  <span className="text-zinc-400 text-xs flex-1">{item.label}</span>
                  <div className="w-24 h-2 rounded-full bg-zinc-700 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${answers.length ? (item.count / answers.length) * 100 : 0}%`, background: item.color, transition: "width 1s" }} /></div>
                  <span className="text-zinc-400 text-xs w-6 text-right">{item.count}</span>
                </div>
              ))}
            </div>
            {sInsight && <div className="rounded-xl bg-zinc-800/40 border border-zinc-700/30 p-4 mt-auto"><p className="text-xs text-emerald-400 font-bold mb-2">KEY MESSAGE</p><p className="text-zinc-400 text-xs leading-relaxed">{sInsight}</p></div>}
          </div>
        </div>
      </div>
    </Slide>
  );
}

function FinalSlide({ audit, insights, active }) {
  const sections = Object.entries(audit.sections || {}).map(([id, s]) => ({ id, ...s })).sort((a, b) => b.score - a.score);
  return (
    <Slide active={active}>
      <div className="flex-1 px-12 pt-8 flex flex-col">
        <h2 className="text-4xl font-black text-white mb-1">Final Audit Summary</h2>
        <div className="w-16 h-1 bg-emerald-400 rounded-full mb-6" />
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-6 text-center">
            <span className="text-xs text-zinc-400">OVERALL SCORE</span>
            <div className="text-6xl font-black mt-2" style={{ color: getBarColor(audit.overall_score) }}><AnimatedNumber value={audit.overall_score} /></div>
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
            <span className="text-xs text-emerald-400 font-bold">TOP STRENGTHS</span>
            {sections.slice(0, 3).map(s => (<div key={s.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0"><span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span><span className="text-emerald-400 font-bold text-sm">{Math.round(s.score)}%</span></div>))}
          </div>
          <div className="rounded-2xl bg-zinc-800/50 border border-zinc-700/50 p-5">
            <span className="text-xs text-pink-400 font-bold">TOP GAPS</span>
            {sections.slice(-3).reverse().map(s => (<div key={s.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0"><span className="text-zinc-300 text-sm">{SECTION_DISPLAY[s.id]?.label || s.section_name}</span><span className="text-pink-400 font-bold text-sm">{Math.round(s.score)}%</span></div>))}
          </div>
        </div>
        <div className="rounded-2xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 p-6 mt-auto mb-4">
          <p className="text-zinc-200 text-sm leading-relaxed text-center italic">"{insights?.closing_statement || "Audit analysis complete."}"</p>
        </div>
        <p className="text-center text-zinc-600 text-xs">Prepared by Digital Impressions | IMPRESS 360 Audit</p>
      </div>
    </Slide>
  );
}

export default function SharedPresentation() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const sectionOrder = ["discover", "mobile", "drive", "dominate", "discourse", "deliver", "delight"];
  const totalSlides = data ? 3 + sectionOrder.filter(id => data.audit?.sections?.[id]).length + 1 : 5;

  const goNext = useCallback(() => setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1)), [totalSlides]);
  const goPrev = useCallback(() => setCurrentSlide(prev => Math.max(prev - 1, 0)), []);

  useEffect(() => {
    axios.get(`${API}/shared/${shareToken}/presentation`)
      .then(resp => setData(resp.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shareToken]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); goNext(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-emerald-400">Loading presentation...</div></div>;
  if (!data?.audit) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-red-400 text-lg">Presentation not found or link expired.</div></div>;

  const { audit, section_definitions, presentation } = data;
  const insights = presentation?.ai_insights || {};
  const activeSections = sectionOrder.filter(id => audit.sections?.[id]);
  
  let si = 0;
  const slides = [];
  slides.push(<CoverSlide key="cover" audit={audit} active={currentSlide === si++} />);
  slides.push(<SectionComparisonSlide key="compare" audit={audit} active={currentSlide === si++} />);
  activeSections.forEach(sid => {
    slides.push(<SectionDetailSlide key={sid} audit={audit} sectionId={sid} questionDefs={section_definitions?.[sid]?.questions} insights={insights} active={currentSlide === si++} />);
  });
  slides.push(<FinalSlide key="final" audit={audit} insights={insights} active={currentSlide === si++} />);
  const total = slides.length;

  return (
    <div ref={containerRef} className="min-h-screen bg-black flex flex-col" data-testid="shared-presentation">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative w-full bg-zinc-950 rounded-xl overflow-hidden shadow-2xl border border-zinc-800/50 presentation-slide-area" style={{ maxWidth: "1280px", aspectRatio: "16/9" }}>
          <div className="absolute inset-0">{slides}</div>
          {currentSlide > 0 && <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-white"><ChevronLeft className="w-5 h-5" /></button>}
          {currentSlide < total - 1 && <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-zinc-800/80 hover:bg-zinc-700 flex items-center justify-center text-white"><ChevronRight className="w-5 h-5" /></button>}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5">
            {Array.from({ length: total }).map((_, i) => (<button key={i} onClick={() => setCurrentSlide(i)} className={cn("h-2 rounded-full transition-all", i === currentSlide ? "w-6 bg-emerald-400" : "w-2 bg-zinc-600")} />))}
          </div>
          <div className="absolute bottom-3 right-4 z-30 text-zinc-500 text-xs">{currentSlide + 1} / {total}</div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 pb-4 no-print">
        <button onClick={() => { containerRef.current?.requestFullscreen(); setIsFullscreen(!isFullscreen); }}
          className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm flex items-center gap-2"><Maximize2 className="w-4 h-4" /> Fullscreen</button>
        <button onClick={() => window.print()} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Download PDF</button>
      </div>
      <p className="text-center text-zinc-700 text-xs pb-2">Powered by IMPRESS 360 Audit | Digital Impressions</p>
    </div>
  );
}
